import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getPropertyVendorsDisplayMap } from 'entities/Vendors/model/selectors/vendorsSelectors';
import { SupplierLogo } from 'shared/ui/SupplierLogo';

const MAX_LOGOS_TO_DISPLAY = 3;
const MAX_NAMES_TO_DISPLAY = 1;
const LOGO_SIZE = 18;

export interface SupplierBadgeItem {
    uid: string;
    sellerUid: string;
    name: string;
}

interface SupplierBadgeProps {
    proposals: SupplierBadgeItem[];
    maxLogos?: number;
    maxNames?: number;
    opacity?: number;
}

export const SupplierBadge = ({
    proposals,
    maxLogos = MAX_LOGOS_TO_DISPLAY,
    maxNames = MAX_NAMES_TO_DISPLAY,
    opacity = 1,
}: SupplierBadgeProps) => {
    const { t } = useTranslation();
    const vendorsDisplayMap = useSelector(getPropertyVendorsDisplayMap);

    const logosToDisplay = useMemo(
        () =>
            proposals
                ?.map((proposal, index) => {
                    if (index >= maxLogos) return null;

                    const vendor = vendorsDisplayMap?.[proposal.sellerUid];
                    if (!vendor) return null;

                    return (
                        <SupplierLogo
                            key={proposal.uid}
                            logo={vendor.logo}
                            brandColor={vendor.brandColor}
                            title={vendor.title}
                            size={LOGO_SIZE}
                            sx={{ marginLeft: index > 0 ? '-3px' : '0px' }}
                        />
                    );
                })
                .filter(Boolean),
        [proposals, vendorsDisplayMap, maxLogos],
    );

    const isCountLabel = (proposals?.length ?? 0) > maxNames;

    const textToDisplay = useMemo(() => {
        if (proposals?.length === 0) return null;

        if (isCountLabel) {
            return `${proposals.length} ${t('suppliers')}`;
        }

        return proposals
            .map((proposal, index) => {
                if (index >= maxNames) return null;
                return proposal.name;
            })
            .filter(Boolean)
            .join(', ');
    }, [isCountLabel, proposals, t, maxNames]);

    if (proposals?.length === 0) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: isCountLabel ? 'center' : 'flex-start',
                gap: '9px',
                ...(isCountLabel
                    ? { flexShrink: 0 }
                    : { minWidth: 0, maxWidth: '100%' }),
            }}
        >
            <Box sx={{ display: 'flex', flexShrink: 0, gap: '0px', opacity }}>
                {logosToDisplay}
            </Box>
            <Box
                sx={{
                    color: '#000',
                    fontSize: '1.3rem',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    opacity,
                    ...(isCountLabel
                        ? { whiteSpace: 'nowrap', flexShrink: 0 }
                        : { minWidth: 0 }),
                }}
            >
                {textToDisplay}
            </Box>
        </Box>
    );
};
